import * as THREE from 'three';
import type { System } from '../../systems';
import { ProjectilePool } from '../combat/ProjectilePool';
import type { PlayerShip } from '../ships';
import type { PlayerStats } from '../player';
import type { AudioManager } from '../../audio';

export interface HostileProjectilesHooks {
  readonly onPlayerHit?: (damage: number) => void;
}

const HIT_COOLDOWN = 0.04;

export class HostileProjectiles implements System {
  readonly id = 'hostile-projectiles';

  private readonly pool: ProjectilePool;
  private readonly shipCenter = new THREE.Vector3();
  private lastHitAt = -1;
  private enabled = true;

  constructor(
    parent: THREE.Object3D,
    private readonly ship: PlayerShip,
    private readonly stats: PlayerStats,
    private readonly audio: AudioManager | null = null,
    private readonly hooks: HostileProjectilesHooks = {}
  ) {
    this.pool = new ProjectilePool(parent, 64);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      for (const p of this.pool.pool) p.deactivate();
    }
  }

  fire(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    speed: number,
    damage: number
  ): void {
    const velocity = direction.clone().multiplyScalar(speed);
    const projectile = this.pool.spawn(origin, velocity, damage);
    if (projectile && this.audio) {
      this.audio.playLaser();
    }
  }

  update(dt: number): void {
    this.pool.update(dt);
    if (!this.enabled) return;
    this.checkPlayerHits(dt);
  }

  dispose(): void {
    this.pool.dispose();
  }

  private checkPlayerHits(_dt: number): void {
    this.ship.getCollisionCenter(this.shipCenter);
    const radius = this.ship.collisionRadius;

    for (const projectile of this.pool.pool) {
      if (!projectile.active) continue;

      const prev = projectile.prevPos;
      const curr = projectile.object3D.position;
      const abx = curr.x - prev.x;
      const aby = curr.y - prev.y;
      const abz = curr.z - prev.z;
      const abLenSq = abx * abx + aby * aby + abz * abz;

      const apx = this.shipCenter.x - prev.x;
      const apy = this.shipCenter.y - prev.y;
      const apz = this.shipCenter.z - prev.z;

      let t = 0;
      if (abLenSq > 1e-6) {
        t = (apx * abx + apy * aby + apz * abz) / abLenSq;
        if (t < 0) t = 0;
        else if (t > 1) t = 1;
      }
      const cx = prev.x + abx * t;
      const cy = prev.y + aby * t;
      const cz = prev.z + abz * t;
      const dx = this.shipCenter.x - cx;
      const dy = this.shipCenter.y - cy;
      const dz = this.shipCenter.z - cz;
      const sum = projectile.radius + radius;
      if (dx * dx + dy * dy + dz * dz >= sum * sum) continue;

      const now = performance.now() / 1000;
      if (now - this.lastHitAt >= HIT_COOLDOWN) {
        // Audio cue is driven by DamageFlashLayer's stats subscription, so
        // we don't fire playHit here — it would double up with the hull
        // damage SFX that triggers from damageHull below.
        this.stats.damageHull(projectile.damage);
        this.hooks.onPlayerHit?.(projectile.damage);
        this.lastHitAt = now;
      }
      projectile.deactivate();
    }
  }
}
